import { useState, useRef, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import { config } from '../config/environment'

// The two organizing tag dimensions (one value per type per session). Generic model on the
// backend (session_tags) means adding a dimension here later needs no schema change.
const TAG_TYPES: { type: string; label: string; color: string }[] = [
  { type: 'client', label: 'Client', color: '#008fa8' },
  { type: 'research_domain', label: 'Research Domain', color: '#7c3aed' },
]

interface SessionTagsProps {
  sessionId?: string
  token: string | null
  /** Tags for the active session, from the sessions list. */
  tags: Record<string, string>
  /** Persisted; lets the parent update its cached session list. */
  onTagsChange: (tags: Record<string, string>) => void
}

export function SessionTags({ sessionId, token, tags, onTagsChange }: SessionTagsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [editType, setEditType] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [options, setOptions] = useState<string[]>([])
  const editTypeRef = useRef<string | null>(null)

  const headers = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) h['Authorization'] = `Bearer ${token}`
    return h
  }, [token])

  // Load autocomplete values for the type being edited.
  useEffect(() => {
    if (!editType) return
    editTypeRef.current = editType
    fetch(`${config.API_CF_URL}/api/r3/tag-values?type=${encodeURIComponent(editType)}`, {
      credentials: 'include', headers: headers(),
    })
      .then(r => r.ok ? r.json() : { values: [] })
      .then((d: { values?: string[] }) => { if (editTypeRef.current === editType) setOptions(d.values || []) })
      .catch(() => { /* best-effort */ })
  }, [editType, headers])

  const save = useCallback(async (type: string, value: string | null) => {
    if (!sessionId) return
    try {
      const res = await fetch(`${config.API_CF_URL}/api/r3/session-tag`, {
        method: 'POST', credentials: 'include', headers: headers(),
        body: JSON.stringify({ sessionId, tagType: type, tagValue: value }),
      })
      if (!res.ok) return
      const data = await res.json() as { tags?: Record<string, string> }
      onTagsChange(data.tags || {})
    } catch { /* best-effort */ }
  }, [sessionId, headers, onTagsChange])

  const openEditor = (e: React.MouseEvent<HTMLElement>, type: string) => {
    setEditType(type)
    setDraft(tags[type] || '')
    setAnchorEl(e.currentTarget)
  }
  const closeEditor = () => { setAnchorEl(null); setEditType(null); setOptions([]) }
  const commit = (value: string) => {
    if (editType) save(editType, value.trim() || null)
    closeEditor()
  }

  if (!sessionId) return null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 3 }}>
      {TAG_TYPES.map(({ type, label, color }) => {
        const value = tags[type]
        return value ? (
          <Box
            key={type}
            onClick={(e) => openEditor(e, type)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
              fontSize: '0.7rem', fontWeight: 600, color, background: `${color}14`,
              px: 0.875, py: 0.25, borderRadius: '6px',
              '&:hover': { background: `${color}22` },
            }}
            title={`${label}: ${value} — click to edit`}
          >
            {value}
            <Box
              component="span"
              onClick={(e) => { e.stopPropagation(); save(type, null) }}
              sx={{ opacity: 0.6, fontWeight: 700, ml: 0.25, '&:hover': { opacity: 1 } }}
              title={`Clear ${label}`}
            >×</Box>
          </Box>
        ) : (
          <Box
            key={type}
            onClick={(e) => openEditor(e, type)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.375, cursor: 'pointer',
              fontSize: '0.68rem', color: '#aaa', border: '1px dashed #d8d4cc',
              px: 0.75, py: 0.2, borderRadius: '6px',
              '&:hover': { color, borderColor: color },
            }}
            title={`Add ${label}`}
          >
            <LocalOfferOutlinedIcon sx={{ fontSize: 12 }} /> {label}
          </Box>
        )
      })}

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeEditor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 240 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#777', mb: 0.75 }}>
            {TAG_TYPES.find(t => t.type === editType)?.label}
          </Typography>
          <Autocomplete
            freeSolo
            openOnFocus
            options={options}
            inputValue={draft}
            onInputChange={(_, v) => setDraft(v)}
            onChange={(_, v) => { if (typeof v === 'string' && v) commit(v) }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                placeholder="Type or pick…"
                inputProps={{
                  ...params.inputProps,
                  // Enter must commit the TYPED text. Handle it on the inner input and stop
                  // propagation, or MUI's own Enter handler selects the highlighted suggestion
                  // instead (typing "Smartly" would silently commit "Novasphere").
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commit(draft) }
                  },
                }}
              />
            )}
          />
        </Box>
      </Popover>
    </Box>
  )
}
